import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  SimpleChanges,
  OnChanges
} from '@angular/core';
import { FormControl } from '@angular/forms';
// Removed HttpClient as we are using the service now
import {
  startWith,
  debounceTime,
  distinctUntilChanged,
  map
} from 'rxjs/operators';
import { Observable } from 'rxjs';
import { PermissionsService } from '../../services/permissions.service';

type UserOrTeam = 'user' | 'team';
type AccessType = 'view' | 'edit';

@Component({
  selector: 'zint-permissions-v2',
  templateUrl: './zint-permissions-v2.component.html',
  styleUrl: './zint-permissions-v2.component.css',
  standalone: false,
})
export class ZintPermissionsV2Component implements OnInit, OnChanges {
  // --- REQUIRED INPUTS ---
  @Input() modelKey!: string;      // e.g., 'organisation_aira_module'
  @Input() objectId!: number;      // e.g., 15
  @Input() accessType: AccessType = 'view'; // 'view' or 'edit'

  // --- OPTIONAL CONFIGURATION ---
  @Input() permissionLabel = '';
  @Input() usersLabel = 'Individuals';
  @Input() teamsLabel = 'Teams';

  // The pool of ALL available options (passed from parent so we don't keep calling that endpoint.)
  @Input() allUsers: Record<string, any>[] = [];
  @Input() allTeams: Record<string, any>[] = [];

  // Emit event if parent needs to know about changes (e.g. for toast notifications)
  @Output() onPermissionUpdated = new EventEmitter<any>();

  // Search Controls
  userSearchControl = new FormControl('');
  teamSearchControl = new FormControl('');

  // UI State
  userFieldFocused = false;
  teamFieldFocused = false;
  isLoading = false;

  // Selected State (IDs only)
  selectedUserIds = new Set<number>();
  selectedTeamIds = new Set<number>();

  // Filtered Lists for Dropdowns
  filteredUsers$: Observable<Record<string, any>[]>;
  filteredTeams$: Observable<Record<string, any>[]>;

  constructor(
      // Removed private http: HttpClient
      private permissionsService: PermissionsService
  ) {}

  ngOnInit(): void {
    this.setupSearchObservables();
    // Only load if inputs are ready
    if (this.modelKey && this.objectId) {
      this.fetchCurrentPermissions();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reload permissions if the object ID changes
    if (changes['objectId'] && !changes['objectId'].firstChange) {
      this.fetchCurrentPermissions();
    }
  }

  // --- 1. API: Fetch Current State ---
  fetchCurrentPermissions(): void {
    this.isLoading = true;

    this.permissionsService.getCurrentObjectPermissions(this.modelKey, this.objectId)
      .subscribe({
        next: (response: any) => {
          // Response structure from Django:
          // { view_access: { users: [], teams: [] }, edit_access: { ... } }

          if (!response) {
            this.isLoading = false;
            return;
          }

          // 1. Determine which part of the response we care about based on @Input accessType
          const accessData = this.accessType === 'view' ? response.view_access : response.edit_access;

          this.selectedUserIds.clear();
          this.selectedTeamIds.clear();

          // 2. Populate Sets
          // Note: The Django view returns lists of objects like {id: 1, email: '...'}
          if (accessData?.users) {
            accessData.users.forEach((u: any) => this.selectedUserIds.add(u.id));
          }
          if (accessData?.teams) {
            accessData.teams.forEach((t: any) => this.selectedTeamIds.add(t.id));
          }

          this.isLoading = false;
        },
        error: (err) => {
          console.error('Error fetching permissions', err);
          this.isLoading = false;
        }
      });
  }

  // --- 2. API: Update Single Permission ---
  togglePermission(item: any, type: UserOrTeam, isChecked: boolean): void {
    // 1. Identify Target and ID
    const targetSet = type === 'user' ? this.selectedUserIds : this.selectedTeamIds;
    // CRITICAL: Identify the correct ID key. 'allUsers' usually has 'userId', 'allTeams' has 'id'.
    const itemId = type === 'user' ? item.userId : item.id;

    // 2. Optimistic UI Update (Update visual state BEFORE API returns)
    if (isChecked) {
      targetSet.add(itemId);
    } else {
      targetSet.delete(itemId);
    }

    const action = isChecked ? 'add' : 'remove';

    // 3. Call Service
    this.permissionsService.updatePermissionsObject(
      this.modelKey,
      this.objectId,
      action,
      type,
      this.accessType,
      itemId
    ).subscribe({
      next: (output) => {
        // Success: The API worked.
        // We notify the parent component in case they want to then action something.
        this.onPermissionUpdated.emit({ success: true, item, action, type });
      },
      error: (err) => {
        // Failure: The API failed (e.g. 403 Forbidden, 500 Error).
        // CRITICAL: Revert the Optimistic Update!
        // If we added it, remove it. If we removed it, add it back.
        if (isChecked) {
          targetSet.delete(itemId);
        } else {
          targetSet.add(itemId);
        }

        console.error('Permission update failed', err);
        // alert('We could not update that permission. Please check your access rights.');
      }
    });
  }

  // --- 3. Helper Logic ---

  // Check if a specific user/team is currently selected (used in template)
  isSelected(item: any, type: UserOrTeam): boolean {
    const id = type === 'user' ? item.userId : item.id;
    return type === 'user' ? this.selectedUserIds.has(id) : this.selectedTeamIds.has(id);
  }

  // Get the actual object list of selected items (for the "bubbles" display)
  getSelectedItemsList(type: UserOrTeam): any[] {
    const sourceList = type === 'user' ? this.allUsers : this.allTeams;
    const targetSet = type === 'user' ? this.selectedUserIds : this.selectedTeamIds;
    if (!sourceList) return [];
    return sourceList.filter(item => {
      const id = type === 'user' ? item.userId : item.id;
      return targetSet.has(id);
    });
  }

  setupSearchObservables(): void {
    this.filteredUsers$ = this.userSearchControl.valueChanges.pipe(
      startWith(''),
      debounceTime(200),
      distinctUntilChanged(),
      map(term => this.filterList(term, 'user'))
    );

    this.filteredTeams$ = this.teamSearchControl.valueChanges.pipe(
      startWith(''),
      debounceTime(200),
      distinctUntilChanged(),
      map(term => this.filterList(term, 'team'))
    );
  }

  filterList(term: string, type: UserOrTeam): any[] {
    const search = (term || '').toLowerCase();
    const list = type === 'user' ? this.allUsers : this.allTeams;

    if (!list) return [];

    return list.filter(item => {
        const key = type === 'user' ? item.email : item.name;
        return key.toLowerCase().includes(search);
    });
  }
}
